import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import "./DashboardPortal.css";
import DashboardIndex from "./DashboardIndex";

// Utility functions for safer data handling
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? null : date.getTime();
};

const createUniqueKey = (item) => {
  const parts = [
    item.sessionId || "",
    item.email || item.keys?.email || item.userId || "",
    item.startTime || "",
    item.endTime || "",
    item.moduleId || "",
    item.functionalityId || "",
  ];
  return btoa(JSON.stringify(parts));
};

const CONSTANTS = {
  SECONDS_IN_DAY: 86400,
  HOURS_IN_WEEK: 168,
  API_TIMEOUT: 600000,
  BASE_URL: "https://showcaseapi.trezi.com",
};

const Analytics = () => {
  // Render counter for debugging
  const renderCount = React.useRef(0);
  renderCount.current += 1;
  console.log("🔄 Dashboard render count:", renderCount.current);

  // State definitions (keeping all existing state)
  const [options1, setOptions1] = useState([]);
  const [options2, setOptions2] = useState([]);
  const [options3, setOptions3] = useState([]);
  const [selectedOption1, setSelectedOption1] = useState("");
  const [selectedOption2, setSelectedOption2] = useState("");
  const [selectedOption3, setSelectedOption3] = useState("");
  const [loadingOptions2, setLoadingOptions2] = useState(false);
  const [loadingOptions3, setLoadingOptions3] = useState(false);
  const [originalResult, setOriginalResult] = useState([]);
  const [filteredResult, setFilteredResult] = useState([]);
  const [filterOptions, setFilterOptions] = useState([]);
  const [totalTimeSpent, setTotalTimeSpent] = useState({
    totalTrue: 0,
    totalFalse: 0,
    totalOther: 0,
    totalTime: 0,
    trueCount: 0,
    falseCount: 0,
    totalHours: 0,
    totalDays: 0,
    remainingHours: 0,
  });
  const [selectedFilter, setSelectedFilter] = useState("");
  const [maxProductName, setMaxProductName] = useState("");
  const [maxUserId, setMaxUserId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [allKeys, setAllKeys] = useState([]);
  const [error, setError] = useState(null);
  const [apiErrors, setApiErrors] = useState({});
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [showModuleComparison, setShowModuleComparison] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [tableSearchTerm, setTableSearchTerm] = useState("");
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  useEffect(() => {
    document.title = "Trezi Dashboard";
  }, []);

  // Keep all the existing functions exactly as they are
  const fetchOptions1 = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        CONSTANTS.API_TIMEOUT
      );

      const response = await fetch(
        "https://lensservice.trezi.com/lensdataservice/api/v1/lens/moduleIds",
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);
      const data = await response.json();

      if (Array.isArray(data)) {
        setOptions1(data);
      } else {
        console.error("Failed to fetch module IDs.");
      }
    } catch (error) {
      console.error("Error fetching module IDs:", error);
    }
  };

  const handleDropdownChange1 = async (event) => {
    const selectedModule = event.target.value;

    setSelectedOption1(selectedModule);
    setSelectedOption2("");
    setSelectedOption3("");
    setOriginalResult("");
    setFilteredResult("");

    fetchOptions2(selectedModule);
  };

  const fetchOptions2 = async (selectedModule) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        CONSTANTS.API_TIMEOUT
      );

      const response = await fetch(
        `https://lensservice.trezi.com/lensdataservice/api/v1/lens/functionalityIds`,
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);
      const data = await response.json();

      if (Array.isArray(data)) {
        let moduleSpecificFunctionalityIds = [];

        if (selectedModule === "TreziLauncher") {
          moduleSpecificFunctionalityIds = data.filter(
            (id) => id === "LauncherSession"
          );
        } else if (selectedModule === "TreziViewer") {
          moduleSpecificFunctionalityIds = data.filter(
            (id) =>
              id !== "LauncherSession" &&
              id !== "LensTeleportHostData" &&
              id !== "LensTeleportAttendeeData"
          );
        } else {
          moduleSpecificFunctionalityIds = data.filter(
            (id) =>
              id !== "LensTeleportHostData" && id !== "LensTeleportAttendeeData"
          );
        }

        const filteredFunctionalityIds = moduleSpecificFunctionalityIds.map(
          (item, index) => ({
            id: index,
            label: item,
          })
        );

        setOptions2(filteredFunctionalityIds);
      } else {
        console.error("Failed to fetch functionality IDs.");
      }

      setLoadingOptions2(false);
    } catch (error) {
      console.error("Error fetching functionality IDs:", error);
      setLoadingOptions2(false);
    }
  };

  const handleDropdownChange2 = (event) => {
    setSelectedOption2(event.target.value);
    setSelectedOption3("");
    setOriginalResult("");
    setFilteredResult("");
    fetchOptions3(selectedOption1, event.target.value);
  };

  const fetchOptions3 = useCallback(
    async (selectedOption1, selectedOption2) => {
      if (!selectedOption1 || !selectedOption2) {
        console.error("Both selectedOption1 and selectedOption2 must be set");
        return;
      }

      setError(null);
      setApiErrors({});
      setLoadingOptions3(true);

      let endpoint = "";

      if (
        selectedOption1 === "TreziLauncher" &&
        selectedOption2 === "LauncherSession"
      ) {
        // Use UUS API for Launcher user emails
        endpoint =
          "https://showcaseapi.trezi.com/uus/api/v1/users/login/data/emails";
      } else {
        endpoint =
          "https://lensservice.trezi.com/lensdataservice/api/v1/lens/userIds";
      }

      try {
        const responses = await Promise.all([
          axios.get(endpoint).catch((err) => ({ data: [], error: err })),
        ]);

        const [response1] = responses;
        const errors = {};

        if (response1.error) {
          errors.primary =
            response1.error.message || "Failed to fetch primary data";
        }
        if (Object.keys(errors).length > 0) {
          setApiErrors(errors);
        }

        let userIds = [];

        if (selectedOption1 === "TreziViewer") {
          if (response1 && Array.isArray(response1.data)) {
            userIds = response1.data
              .map((email) => ({
                id: email,
                label: email,
              }))
              .filter((item) => item.id && item.label);
          } else {
            console.error("Failed to fetch user IDs from endpoint.");
          }
        } else {
          // TreziLauncher - UUS API returns array of emails directly
          if (response1 && Array.isArray(response1.data)) {
            userIds = response1.data
              .map((email) => ({
                id: email,
                label: email,
              }))
              .filter((item) => item.id && item.label);
          } else {
            console.error("Failed to fetch user IDs from UUS endpoint.");
          }
        }

        // Remove duplicates and sort
        const uniqueUserIds = Array.from(
          new Set(userIds.map((item) => item.id))
        ).map((id) => userIds.find((item) => item.id === id));

        uniqueUserIds.sort((a, b) => a.label.localeCompare(b.label));
        setOptions3(uniqueUserIds);
      } catch (error) {
        console.error("Error fetching user IDs:", error);
        setOptions3([]);
      } finally {
        setLoadingOptions3(false);
      }
    },
    []
  );

  useEffect(() => {
    if (selectedOption1 && selectedOption2) {
      fetchOptions3(selectedOption1, selectedOption2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption1, selectedOption2]);

  const handleDropdownChange3 = (event) => {
    setSelectedOption3(event.target.value);
  };

  const uniqueOptions = useMemo(() => {
    if (!Array.isArray(options3) || options3.length === 0) return [];

    const uniqueOptionsSet = new Set();
    const filteredOptions = options3.filter((option) => {
      if (!option?.label) return false;
      const isDuplicate = uniqueOptionsSet.has(option.label);
      uniqueOptionsSet.add(option.label);
      return !isDuplicate;
    });

    return filteredOptions.sort((a, b) =>
      (a.label || "").localeCompare(b.label || "")
    );
  }, [options3]);

  const filteredUserOptions = useMemo(() => {
    if (!userSearchTerm) return uniqueOptions;

    return uniqueOptions.filter((option) =>
      option.label.toLowerCase().includes(userSearchTerm.toLowerCase())
    );
  }, [uniqueOptions, userSearchTerm]);

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
  };

  // Keep the existing handleShowResult function (it's quite long, so keeping as-is)
  const handleShowResult = async () => {
    try {
      setIsLoading(true);

      let apiUrl1 = "https://showcaseapi.trezi.com/uus/api/v1/users/login/data";

      const launcherUrlParams = new URLSearchParams();

      if (startDate) {
        launcherUrlParams.append("startDate", startDate);
      }
      if (endDate) {
        launcherUrlParams.append("endDate", endDate);
      }
      if (selectedOption3 && selectedOption3 !== "All") {
        const normalizedEmail =
          selectedOption3 === "NonTrezi" ? "non-trezi" : selectedOption3;
        launcherUrlParams.append("email", normalizedEmail);
      }

      if (
        selectedOption1 === "TreziLauncher" &&
        selectedOption2 === "LauncherSession" &&
        [...launcherUrlParams].length > 0
      ) {
        apiUrl1 += `?${launcherUrlParams.toString()}`;
      }
      let ViewerUrl = "";

      if (
        selectedOption1 === "TreziLauncher" &&
        selectedOption2 === "LauncherSession"
      ) {
        // Launcher session processing
      } else {
        // Viewer data processing
        ViewerUrl =
          "https://lensservice.trezi.com/lensdataservice/api/v1/lens/data";
      }

      if (!selectedOption2) {
        setError("Please choose functionality Id before showing results.");
        setIsLoading(false);
        return;
      }

      const urlParams = new URLSearchParams();

      if (
        !(
          selectedOption1 === "TreziLauncher" &&
          selectedOption2 === "LauncherSession"
        )
      ) {
        if (selectedOption3) {
          const userIdParam =
            selectedOption3 === "All"
              ? "all"
              : selectedOption3 === "NonTrezi"
              ? "non-trezi"
              : selectedOption3;
          urlParams.append("email", userIdParam);
        }

        urlParams.append("moduleId", selectedOption1);
        urlParams.append("functionalityId", selectedOption2);
      }

      if (startDate) {
        urlParams.append("startTime", startDate);
      }

      if (endDate) {
        urlParams.append("endTime", endDate);
      }

      if ([...urlParams].length > 0 && ViewerUrl) {
        ViewerUrl += `?${urlParams.toString()}`;
      }

      const response1 = apiUrl1
        ? await axios.get(apiUrl1, { timeout: CONSTANTS.API_TIMEOUT })
        : { data: [] };
      const data1 = response1.data?.data || response1.data || [];

      let response3 = { data: [] };

      if (ViewerUrl) {
        response3 = await axios.get(ViewerUrl, {
          timeout: CONSTANTS.API_TIMEOUT,
        });
      }

      const data3 = Array.isArray(response3.data) ? response3.data : [];

      const normalizedData3 = Array.isArray(data3)
        ? data3.map((item) => {
            const start = parseDate(item.startTime) || 0;
            const end = parseDate(item.endTime) || 0;
            let calculatedDuration =
              start && end && end > start ? end - start : 0;

            let finalDuration = item.timeDuration ?? calculatedDuration;

            // Convert seconds to milliseconds if value seems to be in seconds
            if (
              item.timeDuration &&
              item.timeDuration > 0 &&
              item.timeDuration < CONSTANTS.SECONDS_IN_DAY
            ) {
              finalDuration = item.timeDuration * 1000;
            }

            if (item.keys?.timeSpent) {
              const timeSpent = Number(item.keys.timeSpent);
              if (!isNaN(timeSpent) && timeSpent > 0) {
                finalDuration =
                  timeSpent < CONSTANTS.SECONDS_IN_DAY
                    ? timeSpent * 1000
                    : timeSpent;
              }
            }

            return {
              ...item,
              timeDuration: finalDuration,
              keys: {
                ...(item.keys || {}),
                productName: item.keys?.productName || "Lens",
              },
            };
          })
        : [];

      let combinedData = [];

      if (
        selectedOption1 === "TreziLauncher" &&
        selectedOption2 === "LauncherSession"
      ) {
        const convertTimeDuration = (millis) => {
          if (!millis || isNaN(millis)) {
            console.warn("Invalid millis:", millis);
            return "0h 0m 0s";
          }
          const hours = Math.floor(millis / (1000 * 60 * 60));
          const minutes = Math.floor((millis % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((millis % (1000 * 60)) / 1000);
          return `${hours}h ${minutes}m ${seconds}s`;
        };

        combinedData = Array.isArray(data1)
          ? data1.flatMap((user) => {
              if (!user || !Array.isArray(user.sessions)) {
                console.warn("Invalid user data structure:", user);
                return [];
              }
              return user.sessions.map((session) => {
                // API returns startTime/endTime, not loginTime/logOutTime
                const start = session.startTime || session.loginTime;
                const end = session.endTime || session.logOutTime;
                let duration = session.timeDuration;

                if (!duration && start && end) {
                  const startTime = parseDate(start);
                  const endTime = parseDate(end);
                  duration =
                    startTime && endTime && endTime > startTime
                      ? endTime - startTime
                      : 0;
                }

                const originalDuration = duration;
                duration = Number(duration) || 0;

                const durationHours = duration / (1000 * 60 * 60);

                return {
                  email: user.email || user.userId || "Unknown",
                  productName: session.productName || "Lens",
                  loginTime: start,
                  logOutTime: end,
                  timeDuration: duration,
                };
              });
            })
          : [];
      } else {
        // Combine all data sources
        const rawCombinedData = [...data1, ...normalizedData3];

        // Deduplicate based on unique session identifiers
        const deduplicationMap = new Map();

        rawCombinedData.forEach((item, index) => {
          // Create robust unique key to catch duplicates
          const uniqueKey = createUniqueKey(item);

          // Only keep the first occurrence of each unique session
          if (!deduplicationMap.has(uniqueKey)) {
            deduplicationMap.set(uniqueKey, {
              ...item,
              _originalIndex: index,
              _uniqueKey: uniqueKey,
            });
          }
        });

        combinedData = Array.from(deduplicationMap.values());
      }

      if (selectedOption1 && selectedOption2 && ViewerUrl) {
        combinedData = combinedData.filter(
          (item) =>
            item.moduleId === selectedOption1 &&
            item.functionalityId === selectedOption2
        );
      }

      if (combinedData.length > 0) {
        setOriginalResult(combinedData);
        {
          let totalTime = 0;
          let totalTrue = 0;
          let totalFalse = 0;
          let trueCount = 0;
          let falseCount = 0;
          let maxProductCount = {};
          let maxUserCount = {};

          combinedData.forEach((item, index) => {
            let duration = 0;
            let source = "unknown";

            // Use the normalized timeDuration value which already includes proper conversions
            if (item.timeDuration) {
              duration = Number(item.timeDuration);
              source = "timeDuration";
            } else if (item.startTime && item.endTime) {
              const start = parseDate(item.startTime);
              const end = parseDate(item.endTime);
              duration = start && end && end > start ? end - start : 0;
              source = "calculated from start/end";
            }

            const durationHours = duration / (1000 * 60 * 60);

            if (!isNaN(duration) && duration > 0) {
              totalTime += duration;
            }

            if (
              selectedOption1 === "TreziViewer" &&
              selectedOption2 === "LensApplicationModeData" &&
              item.keys
            ) {
              const appMode = item.keys.applicationMode;
              if (appMode === true || appMode === "true" || appMode === "VR") {
                totalTrue += duration;
                trueCount++;
              } else if (
                appMode === false ||
                appMode === "false" ||
                appMode === "Desktop"
              ) {
                totalFalse += duration;
                falseCount++;
              }
            }

            if (
              selectedOption1 === "TreziViewer" &&
              selectedOption2 === "LensShowcaseObjectData" &&
              item.keys
            ) {
              const productName =
                item.keys.productName || item.product || "Lens";
              const userId = item.userId || "Unknown";

              maxProductCount[productName] =
                (maxProductCount[productName] || 0) + 1;
              maxUserCount[userId] = (maxUserCount[userId] || 0) + 1;
            }
          });

          const maxProduct = Object.keys(maxProductCount).reduce(
            (a, b) => (maxProductCount[a] > maxProductCount[b] ? a : b),
            ""
          );
          const maxUser = Object.keys(maxUserCount).reduce(
            (a, b) => (maxUserCount[a] > maxUserCount[b] ? a : b),
            ""
          );

          setTotalTimeSpent({
            totalTime: totalTime,
            totalTrue: totalTrue,
            totalFalse: totalFalse,
            trueCount: trueCount,
            falseCount: falseCount,
            totalOther: 0,
            totalHours: 0,
            totalDays: 0,
            remainingHours: 0,
          });

          setMaxProductName(maxProduct);
          setMaxUserId(maxUser);
        }

        const firstDataItem = combinedData[0];

        if (firstDataItem && firstDataItem.keys && ViewerUrl) {
          const dataKeys = Object.keys(firstDataItem.keys);
          setFilterOptions(dataKeys);
        } else if (firstDataItem && !ViewerUrl) {
          setFilterOptions(["email", "productName", "loginTime", "logOutTime"]);
        } else {
          console.warn("No valid data item found for filter options");
          setFilterOptions([]);
        }
      } else {
        console.log("No data returned from the API");
        const isDateFiltered = startDate || endDate;
        console.warn(
          `No data found${isDateFiltered ? " for the selected date(s)" : ""}.`
        );
        setFilterOptions([]);
        setMaxProductName("");
        setMaxUserId("");
        setOriginalResult([]);
        setFilteredResult([]);
        setTotalTimeSpent({
          totalTrue: 0,
          totalFalse: 0,
          totalOther: 0,
          totalTime: 0,
          trueCount: 0,
          falseCount: 0,
          totalHours: 0,
          totalDays: 0,
          remainingHours: 0,
        });
      }
    } catch (error) {
      console.error("Error fetching result:", error);
      setError(
        `Failed to fetch dashboard data: ${
          error.message || "Unknown error"
        }. Please check your network connection and try again.`
      );
      setIsLoading(false);
      setOriginalResult([]);
      setFilteredResult([]);
      setFilterOptions([]);
      setTotalTimeSpent({
        totalTrue: 0,
        totalFalse: 0,
        totalOther: 0,
        totalTime: 0,
        trueCount: 0,
        falseCount: 0,
        totalHours: 0,
        totalDays: 0,
        remainingHours: 0,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const processAnalyticsData = (data) => {
    if (!data || data.length === 0) return null;

    // User analytics
    const userStats = {};
    const hourlyUsage = Array(24).fill(0);
    const dailyUsage = {};

    data.forEach((item) => {
      const email = item.email || item.keys?.email || item.userId || "Unknown";
      const duration = item.timeDuration || 0;

      // User stats
      if (!userStats[email]) {
        userStats[email] = { totalTime: 0, sessions: 0 };
      }
      userStats[email].totalTime += duration;
      userStats[email].sessions += 1;

      // Hourly usage
      if (item.startTime || item.loginTime) {
        const timeField = item.startTime || item.loginTime;
        const startDate = new Date(timeField);
        if (!isNaN(startDate.getTime())) {
          const hour = startDate.getHours();
          hourlyUsage[hour] += duration;

          // Daily usage
          const dateKey = startDate.toDateString();
          if (!dailyUsage[dateKey]) dailyUsage[dateKey] = 0;
          dailyUsage[dateKey] += duration;
        }
      }
    });

    // Top users
    const topUsers = Object.entries(userStats)
      .map(([email, stats]) => ({ email, ...stats }))
      .sort((a, b) => b.totalTime - a.totalTime)
      .slice(0, 5);

    // Peak hour
    const peakHourIndex = hourlyUsage.indexOf(Math.max(...hourlyUsage));
    const peakHour = `${peakHourIndex}:00 - ${peakHourIndex + 1}:00`;

    // Peak day
    const peakDayEntry = Object.entries(dailyUsage).sort(
      ([, a], [, b]) => b - a
    )[0];
    const peakDayFormatted = peakDayEntry
      ? new Date(peakDayEntry[0]).toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
        })
      : "No data";

    return {
      userStats: Object.entries(userStats).map(([email, stats]) => ({
        email,
        ...stats,
      })),
      topUsers,
      hourlyUsage,
      dailyUsage,
      peakHour: Math.max(...hourlyUsage) > 0 ? peakHour : "No data",
      peakDay: peakDayFormatted,
      totalSessions: data.length,
      totalTime: data.reduce((sum, item) => sum + (item.timeDuration || 0), 0),
    };
  };

  const processModuleComparison = async (startDate, endDate) => {
    try {
      const currentModule = selectedOption1; // TreziLauncher or TreziViewer
      const currentFunctionality = selectedOption2; // LauncherSession or ViewerSession

      // STEP 1: Use the data that's ALREADY LOADED for current module (from originalResult)
      const currentModuleData = {
        totalSessions: originalResult.length,
        totalTime: originalResult.reduce(
          (sum, item) => sum + (item.timeDuration || 0),
          0
        ),
        uniqueUsers: new Set(
          originalResult.map(
            (item) => item.email || item.keys?.email || item.userId || "Unknown"
          )
        ).size,
        functionality: currentFunctionality,
      };

      // STEP 2: Fetch the OTHER module data
      let otherModuleData = {
        totalSessions: 0,
        totalTime: 0,
        uniqueUsers: 0,
        functionality:
          currentModule === "TreziLauncher"
            ? "LensSessionData"
            : "LauncherSession",
      };

      if (currentModule === "TreziLauncher") {
        // Current is Launcher, fetch Viewer data
        let viewerUrl =
          "https://lensservice.trezi.com/lensdataservice/api/v1/lens/data";
        const viewerParams = new URLSearchParams();

        if (selectedOption3) {
          const userIdParam =
            selectedOption3 === "All"
              ? "all"
              : selectedOption3 === "NonTrezi"
              ? "non-trezi"
              : selectedOption3;
          viewerParams.append("email", userIdParam);
        }
        viewerParams.append("moduleId", "TreziViewer");
        viewerParams.append("functionalityId", "LensSessionData");
        if (startDate) viewerParams.append("startTime", startDate);
        if (endDate) viewerParams.append("endTime", endDate);

        if ([...viewerParams].length > 0) {
          viewerUrl += `?${viewerParams.toString()}`;
        }

        try {
          const response = await axios.get(viewerUrl, {
            timeout: CONSTANTS.API_TIMEOUT,
          });

          const viewerSessions = Array.isArray(response.data)
            ? response.data
            : [];

          // Process viewer sessions same as main logic
          const processedSessions = viewerSessions.map((item) => {
            const start = parseDate(item.startTime) || 0;
            const end = parseDate(item.endTime) || 0;
            let calculatedDuration =
              start && end && end > start ? end - start : 0;
            let finalDuration = item.timeDuration ?? calculatedDuration;

            if (
              item.timeDuration &&
              item.timeDuration > 0 &&
              item.timeDuration < CONSTANTS.SECONDS_IN_DAY
            ) {
              finalDuration = item.timeDuration * 1000;
            }

            if (item.keys?.timeSpent) {
              const timeSpent = Number(item.keys.timeSpent);
              if (!isNaN(timeSpent) && timeSpent > 0) {
                finalDuration =
                  timeSpent < CONSTANTS.SECONDS_IN_DAY
                    ? timeSpent * 1000
                    : timeSpent;
              }
            }

            return {
              ...item,
              timeDuration: finalDuration,
            };
          });

          // Deduplicate sessions (same as main logic)
          const deduplicationMap = new Map();
          processedSessions.forEach((item) => {
            const uniqueKey = createUniqueKey(item);
            if (!deduplicationMap.has(uniqueKey)) {
              deduplicationMap.set(uniqueKey, item);
            }
          });
          const deduplicatedSessions = Array.from(deduplicationMap.values());

          // Filter by moduleId and functionalityId (same as main logic)
          const filteredSessions = deduplicatedSessions.filter(
            (item) =>
              item.moduleId === "TreziViewer" &&
              item.functionalityId === "LensSessionData"
          );

          otherModuleData.totalSessions = filteredSessions.length;
          otherModuleData.totalTime = filteredSessions.reduce(
            (sum, item) => sum + (item.timeDuration || 0),
            0
          );

          const userEmails = filteredSessions.map(
            (item) => item.email || item.keys?.email || item.userId || "Unknown"
          );

          otherModuleData.uniqueUsers = new Set(userEmails).size;
        } catch (error) {
          console.error("Error fetching Viewer data:", error.message);
          console.error("Full error:", error);
        }
      } else {
        // Current is Viewer, fetch Launcher data
        let launcherUrl = `${CONSTANTS.BASE_URL}/uus/api/v1/users/login/data`;
        const launcherParams = new URLSearchParams();

        if (selectedOption3 && selectedOption3 !== "All") {
          if (selectedOption3 === "NonTrezi") {
            launcherParams.append("email", "non-trezi");
          } else {
            launcherParams.append("email", selectedOption3);
          }
        }
        if (startDate) launcherParams.append("startDate", startDate);
        if (endDate) launcherParams.append("endDate", endDate);

        if ([...launcherParams].length > 0) {
          launcherUrl += `?${launcherParams.toString()}`;
        }

        try {
          const response = await axios.get(launcherUrl, {
            timeout: CONSTANTS.API_TIMEOUT,
          });

          const launcherData = response.data?.data || response.data || [];

          // Process launcher data same as main logic
          let allSessions = [];
          if (Array.isArray(launcherData)) {
            allSessions = launcherData.flatMap((user) => {
              if (!user || !Array.isArray(user.sessions)) return [];

              return user.sessions.map((session) => {
                // API returns startTime/endTime, not loginTime/logOutTime
                const start = session.startTime || session.loginTime;
                const end = session.endTime || session.logOutTime;
                let duration = session.timeDuration;

                if (!duration && start && end) {
                  const startTime = parseDate(start);
                  const endTime = parseDate(end);
                  duration =
                    startTime && endTime && endTime > startTime
                      ? endTime - startTime
                      : 0;
                }

                return {
                  ...session,
                  email: user.email || user.userId,
                  timeDuration: Number(duration) || 0,
                };
              });
            });
          }

          // Apply NonTrezi filter if needed
          if (selectedOption3 === "NonTrezi") {
            allSessions = allSessions.filter((session) => {
              const email = session.email || "";
              return email && !email.toLowerCase().includes("trezi");
            });
          }

          // NOTE: No deduplication for Launcher data (same as main logic)
          // Launcher API returns unique sessions, no need to deduplicate

          otherModuleData.totalSessions = allSessions.length;
          otherModuleData.totalTime = allSessions.reduce(
            (sum, session) => sum + (session.timeDuration || 0),
            0
          );

          const userEmails = allSessions.map(
            (session) => session.email || session.userId || "Unknown"
          );

          otherModuleData.uniqueUsers = new Set(userEmails).size;
        } catch (error) {
          console.error("Error fetching Launcher data:", {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            url: launcherUrl,
            fullError: error,
          });
        }
      }

      // STEP 3: Return both modules with correct names
      const comparison = {
        LensLauncher:
          currentModule === "TreziLauncher"
            ? {
                totalTime: currentModuleData.totalTime,
                totalSessions: currentModuleData.totalSessions,
                uniqueUsers: currentModuleData.uniqueUsers,
                functionality: "LauncherSession",
              }
            : {
                totalTime: otherModuleData.totalTime,
                totalSessions: otherModuleData.totalSessions,
                uniqueUsers: otherModuleData.uniqueUsers,
                functionality: "LauncherSession",
              },
        LensViewer:
          currentModule === "TreziViewer"
            ? {
                totalTime: currentModuleData.totalTime,
                totalSessions: currentModuleData.totalSessions,
                uniqueUsers: currentModuleData.uniqueUsers,
                functionality: "LensSessionData",
              }
            : {
                totalTime: otherModuleData.totalTime,
                totalSessions: otherModuleData.totalSessions,
                uniqueUsers: otherModuleData.uniqueUsers,
                functionality: "LensSessionData",
              },
      };

      return comparison;
    } catch (error) {
      console.error("Error in processModuleComparison:", error);
      return {
        LensViewer: {
          totalTime: "0hr, 00min, 00sec",
          totalSessions: 0,
          uniqueUsers: 0,
          functionality: "ViewerSession",
        },
        LensLauncher: {
          totalTime: "0hr, 00min, 00sec",
          totalSessions: 0,
          uniqueUsers: 0,
          functionality: "LauncherSession",
        },
      };
    }
  };

  const processFeatureUsageData = (data) => {
    if (!data || data.length === 0) return null;

    const featureUsage = {};
    const featureCategories = {
      camera: ["camera", "snapshot", "photo", "picture", "capture"],
      navigation: ["360", "pano", "panorama", "navigation", "move", "rotate"],
      tools: ["tool", "measure", "annotation", "markup", "edit", "video"],
      settings: ["setting", "config", "preference", "option", "workspace"],
      sharing: ["share", "export", "download", "send"],
      catalog: ["catalog", "catalogue", "library", "gallery"],
    };

    // Create a map to track unique feature uses per session/user
    const uniqueFeatureUses = new Set();

    data.forEach((item) => {
      if (item.keys) {
        const userId = item.userId || item.keys?.email || "Unknown";
        const sessionId = item.sessionId || item._uniqueKey || "session";

        Object.entries(item.keys).forEach(([key, value]) => {
          if (
            key !== "productName" &&
            value &&
            value !== "null" &&
            value !== "" &&
            value !== "0" &&
            value !== 0
          ) {
            // Clean and categorize feature names
            const cleanKey = key.toLowerCase().replace(/[^a-z0-9]/g, "");

            // Try to categorize the feature
            let category = "other";
            for (const [cat, keywords] of Object.entries(featureCategories)) {
              if (
                keywords.some(
                  (keyword) =>
                    cleanKey.includes(keyword) ||
                    key.toLowerCase().includes(keyword)
                )
              ) {
                category = cat;
                break;
              }
            }

            const featureName = key
              .replace(/([A-Z])/g, " $1")
              .replace(/^./, (str) => str.toUpperCase())
              .trim();

            // Create unique identifier for this feature usage
            const uniqueId = `${userId}-${sessionId}-${featureName}`;

            if (!uniqueFeatureUses.has(uniqueId)) {
              uniqueFeatureUses.add(uniqueId);

              if (!featureUsage[featureName]) {
                featureUsage[featureName] = {
                  count: 0,
                  category,
                  users: new Set(),
                  sessions: new Set(),
                };
              }

              // Count based on the actual value if it's numeric
              let usageCount = 1;
              if (!isNaN(value) && Number(value) > 0) {
                usageCount = Number(value);
              }

              featureUsage[featureName].count += usageCount;
              featureUsage[featureName].users.add(userId);
              featureUsage[featureName].sessions.add(sessionId);
            }
          }
        });
      }
    });

    // Convert to array and sort by usage
    const featuresArray = Object.entries(featureUsage)
      .map(([name, data]) => ({
        name,
        count: data.count,
        category: data.category,
        uniqueUsers: data.users.size,
        uniqueSessions: data.sessions ? data.sessions.size : 1,
        percentage: 0, // Will be calculated below
      }))
      .sort((a, b) => b.count - a.count);

    // Calculate percentages and totals
    const totalUsage = featuresArray.reduce(
      (sum, feature) => sum + feature.count,
      0
    );

    const allUsers = new Set();
    const allSessions = new Set();
    Object.values(featureUsage).forEach((data) => {
      data.users.forEach((user) => allUsers.add(user));
      if (data.sessions) {
        data.sessions.forEach((session) => allSessions.add(session));
      }
    });

    featuresArray.forEach((feature) => {
      feature.percentage =
        totalUsage > 0 ? ((feature.count / totalUsage) * 100).toFixed(1) : 0;
    });

    return {
      features: featuresArray,
      totalFeatures: featuresArray.length,
      totalUsage,
      totalUsers: allUsers.size,
      totalSessions: allSessions.size,
      topFeature: featuresArray[0] || null,
      categoryStats: featuresArray.reduce((acc, feature) => {
        if (!acc[feature.category])
          acc[feature.category] = { count: 0, features: [] };
        acc[feature.category].count += feature.count;
        acc[feature.category].features.push(feature.name);
        return acc;
      }, {}),
    };
  };

  const handleShowAnalytics = async () => {
    // Use originalResult for analytics (shows all data with main filters, not table search)
    const analytics = processAnalyticsData(originalResult);

    // Add feature usage analytics if this is LensFeatureUsageData
    let featureAnalytics = null;
    if (
      selectedOption1 === "TreziViewer" &&
      selectedOption2 === "LensFeatureUsageData"
    ) {
      featureAnalytics = processFeatureUsageData(originalResult);
    }

    // Always add module comparison for both LensViewer and LensLauncher
    // This will fetch fresh data for both modules with current filters
    let moduleComparison = null;
    if (
      selectedOption1 === "TreziViewer" ||
      selectedOption1 === "TreziLauncher"
    ) {
      try {
        moduleComparison = await processModuleComparison(startDate, endDate);
      } catch (error) {
        console.error("Failed to fetch module comparison:", error);
        // Still show empty comparison to indicate the feature is available
        moduleComparison = {
          LensViewer: {
            totalTime: 0,
            totalSessions: 0,
            uniqueUsers: 0,
            functionality: "ViewerSession",
          },
          LensLauncher: {
            totalTime: 0,
            totalSessions: 0,
            uniqueUsers: 0,
            functionality: "LauncherSession",
          },
        };
      }
    }

    setAnalyticsData({
      ...analytics,
      featureUsage: featureAnalytics,
      moduleComparison,
    });
    setShowAnalytics(true);
  };

  const formatTime = (millis) => {
    if (!millis || isNaN(millis) || millis <= 0) return "0hr, 00min, 00sec";

    let totalMs = Number(millis);
    console.log(`formatTime input: ${millis}, converted to: ${totalMs}`);

    if (totalMs < 1000 && totalMs > 0 && totalMs < 60) {
      totalMs = totalMs * 1000;
      console.log(`Converted small value to milliseconds: ${totalMs}`);
    } else if (totalMs >= 1000000000) {
      console.warn(
        `Unusually large time value: ${totalMs}, treating as milliseconds`
      );
    }

    const totalSeconds = Math.floor(totalMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${hours}hr, ${minutes.toString().padStart(2, "0")}min, ${seconds
      .toString()
      .padStart(2, "0")}sec`;
  };

  useEffect(() => {
    fetchOptions1();
  }, []);

  useEffect(() => {
    if (originalResult && selectedFilter) {
      const filtered = originalResult.map((data) => ({
        moduleId: data.moduleId,
        functionalityId: data.functionalityId,
        userId: data.userId,
        sessionId: data.sessionId || data._uniqueKey || "N/A",
        startTime: data.startTime,
        endTime: data.endTime,
        keys: Object.keys(data.keys)
          .filter((key) => key === selectedFilter)
          .reduce((obj, key) => {
            obj[key] = data.keys[key];
            return obj;
          }, {}),
      }));
      setFilteredResult(filtered);
    } else {
      setFilteredResult(originalResult);
    }
  }, [originalResult, selectedFilter]);

  const filteredTableData = useMemo(() => {
    if (!tableSearchTerm || !filteredResult || !Array.isArray(filteredResult)) {
      return filteredResult;
    }

    const searchLower = tableSearchTerm.toLowerCase();
    return filteredResult.filter((item) => {
      // Search in all string fields
      const searchableValues = [
        item.email,
        item.userId,
        item.moduleId,
        item.functionalityId,
        item.productName,
        item.sessionId,
        item.startTime,
        item.endTime,
        item.loginTime,
        item.logOutTime,
        ...(item.keys ? Object.values(item.keys).map((v) => String(v)) : []),
      ];

      return searchableValues.some((value) =>
        String(value).toLowerCase().includes(searchLower)
      );
    });
  }, [filteredResult, tableSearchTerm]);

  const exportToExcel = () => {
    try {
      const table = document.querySelector(".result-table");

      if (!table) {
        console.error("No table found to export.");
        return;
      }

      const data = [];

      const headings = [];
      const headerCells = table.querySelectorAll("th");
      if (headerCells.length === 0) {
        console.error("No table headers found.");
        return;
      }

      headerCells.forEach((th) => {
        headings.push(th.textContent || "");
      });
      data.push(headings);

      const rows = table.querySelectorAll("tbody tr");
      rows.forEach((row) => {
        const rowData = [];
        const cells = row.querySelectorAll("td");

        cells.forEach((cell) => {
          rowData.push(cell.textContent || "");
        });

        if (rowData.length > 0) {
          data.push(rowData);
        }
      });

      if (data.length <= 1) {
        console.error("No data rows found to export.");
        return;
      }

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, "Analytics_Data");

      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-");
      const filename = `analytics_export_${timestamp}.xlsx`;

      XLSX.writeFile(wb, filename);
      console.log("Data exported successfully");
    } catch (error) {
      console.error("Error exporting data to Excel:", error);
    }
  };

  const getOptionLabel1 = (moduleId) => {
    switch (moduleId) {
      case "TreziViewer":
        return "LensViewer";
      case "TreziLauncher":
        return "LensLauncher";
      default:
        return moduleId;
    }
  };

  const getOptionValue1 = (moduleId) => {
    switch (moduleId) {
      case "LensViewer":
        return "TreziViewer";
      case "LensLauncher":
        return "TreziLauncher";
      default:
        return moduleId;
    }
  };

  const getOptionLabel = (label) => {
    switch (label) {
      case "LensSessionData":
        return "ViewerSession";
      case "LensApplicationModeData":
        return "VR/Desktop";
      case "LensShowcaseObjectData":
        return "ShowcaseObject";
      case "LensFeatureUsageData":
        return "FeatureUsage";
      default:
        return label;
    }
  };

  const getOptionValue = (label) => {
    switch (label) {
      case "ViewerSession":
        return "LensSessionData";
      case "VR/Desktop":
        return "LensApplicationModeData";
      case "ShowcaseObject":
        return "LensShowcaseObjectData";
      case "FeatureUsage":
        return "LensFeatureUsageData";
      default:
        return label;
    }
  };

  const formatDateIST = (timestamp) => {
    if (!timestamp) return "N/A";

    const date = new Date(timestamp);
    if (isNaN(date)) return "Invalid Date";

    return date.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const formatDateUTCSummary = (date) => {
    const utcDate = new Date(date);
    const month = String(utcDate.getUTCMonth() + 1).padStart(2, "0");
    const day = String(utcDate.getUTCDate()).padStart(2, "0");
    const year = utcDate.getUTCFullYear();
    return `${month}/${day}/${year}`;
  };

  // Removed automatic LensFeatureUsageData fetch - data will be fetched only when user clicks "Show Result" button
  // This optimization reduces unnecessary API calls and associated costs

  const formatTimeDuration = (millis) => {
    if (!millis || isNaN(millis)) return "0hr, 00min, 00sec";
    const totalSeconds = Math.floor(millis / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours}hr, ${minutes}min, ${seconds}sec`;
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return "Invalid Date";
    return dateObj.getTime() === 0 ? "N/A" : dateObj.toLocaleDateString();
  };

  useEffect(() => {
    if (
      filteredResult &&
      Array.isArray(filteredResult) &&
      filteredResult.length > 0
    ) {
      const allKeysSet = new Set();
      filteredResult.forEach((item) => {
        if (item && item.keys && typeof item.keys === "object") {
          Object.keys(item.keys).forEach((key) => {
            if (key !== "productName" && key) {
              allKeysSet.add(key);
            }
          });
        }
      });
      setAllKeys(Array.from(allKeysSet).sort());
    } else {
      setAllKeys([]);
    }
  }, [filteredResult]);

  console.log("filteredResult:", filteredResult);

  return (
    <>
      <DashboardIndex />

      {error && (
        <div
          className="error-banner"
          style={{
            backgroundColor: "#ffeaa7",
            color: "#e17055",
            padding: "12px",
            margin: "10px 0",
            borderRadius: "8px",
            border: "1px solid #fdcb6e",
          }}
        >
          <strong>Error:</strong> {error}
          <button
            onClick={() => setError(null)}
            style={{
              marginLeft: "10px",
              background: "none",
              border: "none",
              color: "#e17055",
              cursor: "pointer",
              fontSize: "16px",
            }}
          >
            ×
          </button>
        </div>
      )}

      {Object.keys(apiErrors).length > 0 && (
        <div
          className="api-errors"
          style={{
            backgroundColor: "#fab1a0",
            color: "#e84393",
            padding: "10px",
            margin: "10px 0",
            borderRadius: "6px",
            fontSize: "14px",
          }}
        >
          <strong>API Issues:</strong>
          <ul style={{ margin: "5px 0", paddingLeft: "20px" }}>
            {Object.entries(apiErrors).map(([key, error]) => (
              <li key={key}>
                {key}: {error}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="app-minimal">
        <div className="dashboard-minimal">
          <div className="header-minimal">
            <h2>Analytics Dashboard</h2>
          </div>

          {isLoading && (
            <div className="loading-minimal">
              <div className="loading-spinner"></div>
              <div className="loading-text">Loading analytics data...</div>
            </div>
          )}

          {!isLoading && (
            <div className="controls-minimal">
              <div className="controls-row">
                <div className="control-item">
                  <label>Module:</label>
                  <select
                    value={selectedOption1}
                    onChange={handleDropdownChange1}
                  >
                    <option value="">Choose Module</option>
                    {options1.map((moduleId, index) => (
                      <option key={index} value={getOptionValue1(moduleId)}>
                        {getOptionLabel1(moduleId)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="control-item">
                  <label>Functionality:</label>
                  <select
                    value={selectedOption2}
                    onChange={handleDropdownChange2}
                    disabled={loadingOptions2}
                  >
                    <option value="">
                      {loadingOptions2
                        ? "Loading functionalities..."
                        : "Select Functionality"}
                    </option>
                    {!loadingOptions2 &&
                      options2.map((optionFunctionality, index) => (
                        <option
                          key={optionFunctionality.id || index}
                          value={getOptionValue(optionFunctionality.label)}
                        >
                          {getOptionLabel(optionFunctionality.label)}
                        </option>
                      ))}
                    {selectedOption1 === "TreziViewer" && (
                      <option
                        key="LensFeatureUsageData"
                        value="LensFeatureUsageData"
                      >
                        LensFeatureUsageData
                      </option>
                    )}
                    {selectedOption1 === "TreziLauncher" && (
                      <option key="LauncherSession" value="LauncherSession">
                        LauncherSession
                      </option>
                    )}
                  </select>
                </div>

                <div className="control-item">
                  <label>User:</label>
                  <div style={{ position: "relative" }}>
                    <input
                      type="text"
                      className="user-search-input"
                      placeholder="Type to search..."
                      value={
                        userSearchTerm ||
                        (selectedOption3 === "All"
                          ? "All"
                          : selectedOption3 === "NonTrezi"
                          ? "Non Trezi"
                          : filteredUserOptions.find(
                              (opt) => opt.id === selectedOption3
                            )?.label || "")
                      }
                      onChange={(e) => {
                        setUserSearchTerm(e.target.value);
                        setShowUserDropdown(true);
                      }}
                      onFocus={() => setShowUserDropdown(true)}
                      disabled={loadingOptions3}
                    />
                    {showUserDropdown && !loadingOptions3 && (
                      <div
                        className="custom-dropdown"
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          backgroundColor: "white",
                          border: "1px solid #e0e0e0",
                          borderRadius: "4px",
                          maxHeight: "200px",
                          overflowY: "auto",
                          zIndex: 1000,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                          marginTop: "2px",
                        }}
                      >
                        <div
                          onClick={() => {
                            setSelectedOption3("All");
                            setUserSearchTerm("");
                            setShowUserDropdown(false);
                          }}
                          style={{
                            padding: "8px 12px",
                            cursor: "pointer",
                            fontSize: "13px",
                            backgroundColor:
                              selectedOption3 === "All" ? "#f3f4f6" : "white",
                          }}
                          onMouseEnter={(e) =>
                            (e.target.style.backgroundColor = "#f3f4f6")
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.backgroundColor =
                              selectedOption3 === "All" ? "#f3f4f6" : "white")
                          }
                        >
                          All
                        </div>
                        <div
                          onClick={() => {
                            setSelectedOption3("NonTrezi");
                            setUserSearchTerm("");
                            setShowUserDropdown(false);
                          }}
                          style={{
                            padding: "8px 12px",
                            cursor: "pointer",
                            fontSize: "13px",
                            backgroundColor:
                              selectedOption3 === "NonTrezi"
                                ? "#f3f4f6"
                                : "white",
                          }}
                          onMouseEnter={(e) =>
                            (e.target.style.backgroundColor = "#f3f4f6")
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.backgroundColor =
                              selectedOption3 === "NonTrezi"
                                ? "#f3f4f6"
                                : "white")
                          }
                        >
                          Non Trezi
                        </div>
                        {filteredUserOptions.length > 0 ? (
                          filteredUserOptions.map((option) => (
                            <div
                              key={option.id}
                              onClick={() => {
                                setSelectedOption3(option.id);
                                setUserSearchTerm("");
                                setShowUserDropdown(false);
                              }}
                              style={{
                                padding: "8px 12px",
                                cursor: "pointer",
                                fontSize: "13px",
                                backgroundColor:
                                  selectedOption3 === option.id
                                    ? "#f3f4f6"
                                    : "white",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.backgroundColor = "#f3f4f6")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.backgroundColor =
                                  selectedOption3 === option.id
                                    ? "#f3f4f6"
                                    : "white")
                              }
                            >
                              {option.label}
                            </div>
                          ))
                        ) : (
                          <div
                            style={{
                              padding: "8px 12px",
                              fontSize: "13px",
                              color: "#9ca3af",
                            }}
                          >
                            {userSearchTerm
                              ? "No matching users"
                              : "No options available"}
                          </div>
                        )}
                      </div>
                    )}
                    {showUserDropdown && (
                      <div
                        style={{
                          position: "fixed",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          zIndex: 999,
                        }}
                        onClick={() => setShowUserDropdown(false)}
                      />
                    )}
                  </div>
                </div>

                {originalResult &&
                  Array.isArray(originalResult) &&
                  originalResult.length > 0 &&
                  selectedOption1 === "TreziViewer" && (
                    <div className="control-item">
                      <label>Filter:</label>
                      <select
                        onChange={(e) => setSelectedFilter(e.target.value)}
                      >
                        <option value="">All</option>
                        {filterOptions
                          .sort((a, b) => a.localeCompare(b))
                          .map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                      </select>
                    </div>
                  )}

                <div className="control-item">
                  <label>Start:</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={handleStartDateChange}
                  />
                </div>

                <div className="control-item">
                  <label>End:</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={handleEndDateChange}
                  />
                </div>

                <div className="control-item">
                  <button
                    onClick={handleShowResult}
                    disabled={!selectedOption1 || !selectedOption2 || isLoading}
                  >
                    {isLoading ? "Loading..." : "Show"}
                  </button>
                </div>

                <div className="control-item">
                  <button
                    onClick={exportToExcel}
                    disabled={
                      !filteredResult ||
                      !Array.isArray(filteredResult) ||
                      filteredResult.length === 0
                    }
                  >
                    Export
                  </button>
                </div>

                <div className="control-item">
                  <button
                    onClick={handleShowAnalytics}
                    disabled={
                      !filteredResult ||
                      !Array.isArray(filteredResult) ||
                      filteredResult.length === 0
                    }
                  >
                    Analytics
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="summary-minimal">
            {originalResult &&
              Array.isArray(originalResult) &&
              originalResult.length > 0 && (
                <div className="summary-stats">
                  <span className="stat">
                    Sessions: {filteredResult ? filteredResult.length : 0}
                  </span>
                  <span className="stat">
                    Total: {formatTime(totalTimeSpent.totalTime)}
                  </span>
                  {selectedOption2 === "LensApplicationModeData" && (
                    <>
                      <span className="stat">
                        VR: {formatTime(totalTimeSpent.totalTrue)} (
                        {totalTimeSpent.trueCount})
                      </span>
                      <span className="stat">
                        Desktop: {formatTime(totalTimeSpent.totalFalse)} (
                        {totalTimeSpent.falseCount})
                      </span>
                    </>
                  )}
                  {selectedOption2 === "LensShowcaseObjectData" && (
                    <>
                      <span className="stat">
                        Max Product: {maxProductName}
                      </span>
                      <span className="stat">Max User: {maxUserId}</span>
                    </>
                  )}
                </div>
              )}
          </div>

          <div className="results-minimal">
            {filteredResult &&
              Array.isArray(filteredResult) &&
              filteredResult.length > 0 && (
                <div style={{ marginBottom: "15px" }}>
                  <input
                    type="text"
                    placeholder="Search in table..."
                    value={tableSearchTerm}
                    onChange={(e) => setTableSearchTerm(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px 15px",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      fontSize: "14px",
                      boxSizing: "border-box",
                    }}
                  />
                  {tableSearchTerm && (
                    <div
                      style={{
                        marginTop: "5px",
                        fontSize: "12px",
                        color: "#666",
                      }}
                    >
                      Showing {filteredTableData?.length || 0} of{" "}
                      {filteredResult.length} results
                    </div>
                  )}
                </div>
              )}

            {selectedOption1 === "TreziViewer" &&
              selectedOption2 !== "LensFeatureUsageData" &&
              filteredTableData &&
              Array.isArray(filteredTableData) &&
              filteredTableData.length > 0 && (
                <div className="table-container">
                  <table className="result-table">
                    <thead>
                      <tr>
                        <th>Module</th>
                        <th>Functionality</th>
                        <th>User</th>
                        <th>Session</th>
                        <th>Start Time</th>
                        <th>End Time</th>
                        <th>Product</th>
                        {filteredTableData[0] &&
                          filteredTableData[0].keys &&
                          Object.keys(filteredTableData[0].keys)
                            .filter((key) => key !== "productName")
                            .map((key) => <th key={key}>{key}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {[...filteredTableData]
                        .sort(
                          (a, b) =>
                            new Date(b.startTime) - new Date(a.startTime)
                        )
                        .map((data, index) => (
                          <tr key={index}>
                            <td>{data.moduleId}</td>
                            <td>{data.functionalId}</td>
                            <td>{data.userId}</td>
                            <td>{data.sessionId}</td>
                            <td>{formatDateIST(data.startTime)}</td>
                            <td>{formatDateIST(data.endTime)}</td>
                            <td>{data.product || "Lens"}</td>
                            {Object.entries(data.keys || {})
                              .filter(([key]) => key !== "productName")
                              .map(([key, value], idx) => (
                                <td
                                  key={idx}
                                  title={
                                    key === "timeSpent"
                                      ? `Raw: ${value}, Used: ${
                                          data.timeDuration || value
                                        }`
                                      : ""
                                  }
                                >
                                  {key === "timeSpent"
                                    ? formatTime(data.timeDuration || value)
                                    : String(value)}
                                </td>
                              ))}
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}

            {selectedOption1 === "TreziLauncher" &&
              filteredTableData &&
              Array.isArray(filteredTableData) &&
              filteredTableData.length > 0 && (
                <div className="table-container">
                  <table className="result-table">
                    <thead>
                      <tr>
                        <th>Module</th>
                        <th>Functionality</th>
                        <th>User</th>
                        <th>Login</th>
                        <th>Logout</th>
                        <th>Duration</th>
                        <th>Product</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTableData
                        .sort(
                          (a, b) =>
                            new Date(b.loginTime) - new Date(a.loginTime)
                        )
                        .map((data, index) => {
                          const loginDate = new Date(
                            data.loginTime
                          ).toLocaleString();
                          const logOutDate = new Date(
                            data.logOutTime
                          ).toLocaleString();
                          const isLongSession =
                            data.timeDuration > 1000 * 60 * 60 * 24 * 7;
                          return (
                            <tr key={index}>
                              <td>LensLauncher</td>
                              <td>LauncherSession</td>
                              <td>{data.email}</td>
                              <td>{loginDate}</td>
                              <td>
                                {logOutDate === "1/1/1970, 5:30:00 AM"
                                  ? "NULL"
                                  : logOutDate}
                              </td>
                              <td
                                style={{
                                  color: isLongSession ? "#dc2626" : "inherit",
                                }}
                                title={`${data.timeDuration}ms (${(
                                  data.timeDuration /
                                  (1000 * 60 * 60)
                                ).toFixed(2)}h)`}
                              >
                                {formatTime(data.timeDuration)}
                                {isLongSession ? " (!)" : ""}
                              </td>
                              <td>{data.productName || "Lens"}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}

            {selectedOption2 === "LensFeatureUsageData" &&
              filteredTableData &&
              Array.isArray(filteredTableData) &&
              filteredTableData.length > 0 && (
                <div className="table-container">
                  <table className="result-table">
                    <thead>
                      <tr>
                        <th>Module</th>
                        <th>Functionality</th>
                        <th>User</th>
                        <th>Session</th>
                        <th>Start</th>
                        <th>End</th>
                        {allKeys.map((key) => (
                          <th key={key}>{key}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTableData.map((data, index) => (
                        <tr key={index}>
                          <td>{data.moduleId}</td>
                          <td>{data.functionalityId}</td>
                          <td>{data.userId}</td>
                          <td>{data.sessionId}</td>
                          <td>{new Date(data.startTime).toLocaleString()}</td>
                          <td>{new Date(data.endTime).toLocaleString()}</td>
                          {allKeys.map((key) => (
                            <td key={key}>{data.keys?.[key] ?? "-"}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Analytics Modal */}
      {showAnalytics && analyticsData && (
        <div
          className="analytics-modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            className="analytics-modal"
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "30px",
              maxWidth: "90vw",
              maxHeight: "90vh",
              overflow: "auto",
              position: "relative",
              boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
            }}
          >
            <button
              onClick={() => setShowAnalytics(false)}
              style={{
                position: "absolute",
                top: "15px",
                right: "15px",
                background: "none",
                border: "none",
                fontSize: "24px",
                cursor: "pointer",
                color: "#666",
              }}
            >
              ×
            </button>

            <div className="analytics-content">
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  padding: "30px",
                  borderRadius: "15px",
                  marginBottom: "30px",
                  boxShadow: "0 8px 32px rgba(102, 126, 234, 0.2)",
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    color: "white",
                    fontSize: "32px",
                    fontWeight: "700",
                    textAlign: "center",
                    letterSpacing: "0.5px",
                    textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  Analytics Dashboard
                </h2>
                <p
                  style={{
                    margin: "10px 0 0 0",
                    color: "rgba(255, 255, 255, 0.9)",
                    fontSize: "14px",
                    textAlign: "center",
                    fontWeight: "400",
                  }}
                >
                  {analyticsData.totalSessions} Sessions •{" "}
                  {analyticsData.uniqueUsers} Users •{" "}
                  {selectedOption1 || "All Modules"}
                </p>
              </div>

              {/* Summary Cards */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "20px",
                  marginBottom: "30px",
                }}
              >
                <div
                  style={{
                    backgroundColor: "#3b82f6",
                    color: "white",
                    padding: "20px",
                    borderRadius: "10px",
                    textAlign: "center",
                    boxShadow: "0 4px 6px rgba(59, 130, 246, 0.3)",
                  }}
                >
                  <h3 style={{ margin: "0 0 10px 0", fontSize: "16px" }}>
                    Total Sessions
                  </h3>
                  <p
                    style={{ margin: 0, fontSize: "24px", fontWeight: "bold" }}
                  >
                    {analyticsData.totalSessions}
                  </p>
                </div>
                <div
                  style={{
                    backgroundColor: "#10b981",
                    color: "white",
                    padding: "20px",
                    borderRadius: "10px",
                    textAlign: "center",
                    boxShadow: "0 4px 6px rgba(16, 185, 129, 0.3)",
                  }}
                >
                  <h3 style={{ margin: "0 0 10px 0", fontSize: "16px" }}>
                    Total Time
                  </h3>
                  <p
                    style={{ margin: 0, fontSize: "24px", fontWeight: "bold" }}
                  >
                    {formatTime(analyticsData.totalTime)}
                  </p>
                </div>
                <div
                  style={{
                    backgroundColor: "#8b5cf6",
                    color: "white",
                    padding: "20px",
                    borderRadius: "10px",
                    textAlign: "center",
                    boxShadow: "0 4px 6px rgba(139, 92, 246, 0.3)",
                  }}
                >
                  <h3 style={{ margin: "0 0 10px 0", fontSize: "16px" }}>
                    Peak Hour
                  </h3>
                  <p
                    style={{ margin: 0, fontSize: "18px", fontWeight: "bold" }}
                  >
                    {analyticsData.peakHour}
                  </p>
                </div>
                <div
                  style={{
                    backgroundColor: "#f59e0b",
                    color: "white",
                    padding: "20px",
                    borderRadius: "10px",
                    textAlign: "center",
                    boxShadow: "0 4px 6px rgba(245, 158, 11, 0.3)",
                  }}
                >
                  <h3 style={{ margin: "0 0 10px 0", fontSize: "16px" }}>
                    Peak Day
                  </h3>
                  <p
                    style={{ margin: 0, fontSize: "16px", fontWeight: "bold" }}
                  >
                    {analyticsData.peakDay}
                  </p>
                </div>
              </div>

              {/* Top Users */}
              <div style={{ marginBottom: "30px" }}>
                <h3 style={{ marginBottom: "15px", color: "#333" }}>
                  Top 5 Users
                </h3>
                <div style={{ display: "grid", gap: "8px" }}>
                  {analyticsData.topUsers.map((user, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 16px",
                        backgroundColor: "#f8fafc",
                        borderRadius: "6px",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <div>
                        <span
                          style={{
                            fontSize: "14px",
                            marginRight: "12px",
                            color: "#64748b",
                            fontWeight: "500",
                          }}
                        >
                          #{index + 1}
                        </span>
                        <span style={{ fontWeight: "600", color: "#1e293b" }}>
                          {user.email}
                        </span>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontWeight: "500", color: "#1e293b" }}>
                          {formatTime(user.totalTime)}
                        </div>
                        <small style={{ color: "#64748b" }}>
                          {user.sessions} sessions
                        </small>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Hourly Usage Chart */}
              <div style={{ marginBottom: "30px" }}>
                <h3 style={{ marginBottom: "15px", color: "#333" }}>
                  Hourly Usage Pattern
                </h3>
                {Math.max(...analyticsData.hourlyUsage) > 0 ? (
                  <>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "end",
                        height: "200px",
                        backgroundColor: "#f8fafc",
                        padding: "20px",
                        borderRadius: "10px",
                        gap: "3px",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      {analyticsData.hourlyUsage.map((usage, hour) => {
                        const maxUsage = Math.max(...analyticsData.hourlyUsage);
                        const height =
                          maxUsage > 0
                            ? Math.max(
                                (usage / maxUsage) * 150,
                                usage > 0 ? 5 : 0
                              )
                            : 0;
                        const isPeakHour = usage === maxUsage && usage > 0;
                        return (
                          <div
                            key={hour}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              flex: 1,
                              minHeight: "40px",
                            }}
                          >
                            <div
                              style={{
                                width: "100%",
                                minWidth: "8px",
                                height: `${height}px`,
                                backgroundColor: isPeakHour
                                  ? "#dc2626"
                                  : usage > 0
                                  ? "#3b82f6"
                                  : "#e5e7eb",
                                borderRadius: "4px 4px 0 0",
                                marginBottom: "5px",
                                transition: "all 0.2s ease",
                                cursor: "pointer",
                                opacity: usage > 0 ? 1 : 0.3,
                              }}
                              title={`${hour}:00-${hour + 1}:00: ${
                                usage > 0 ? formatTime(usage) : "No usage"
                              }`}
                            />
                            <small
                              style={{
                                fontSize: "9px",
                                color: isPeakHour ? "#dc2626" : "#64748b",
                                fontWeight: isPeakHour ? "bold" : "normal",
                                transform: "rotate(-45deg)",
                                transformOrigin: "center",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {hour}h
                            </small>
                          </div>
                        );
                      })}
                    </div>
                    <p
                      style={{
                        textAlign: "center",
                        color: "#64748b",
                        fontSize: "12px",
                        marginTop: "10px",
                      }}
                    >
                      Hover over bars for details. Red indicates peak hour.
                    </p>
                  </>
                ) : (
                  <div
                    style={{
                      padding: "40px",
                      textAlign: "center",
                      backgroundColor: "#f8fafc",
                      borderRadius: "10px",
                      border: "1px solid #e2e8f0",
                      color: "#64748b",
                    }}
                  >
                    <p>No hourly usage data available</p>
                  </div>
                )}
              </div>

              {/* Feature Usage Analytics - Only for LensFeatureUsageData */}
              {analyticsData.featureUsage && (
                <div style={{ marginBottom: "30px" }}>
                  <h3 style={{ marginBottom: "20px", color: "#333" }}>
                    Feature Usage Analytics
                  </h3>

                  {/* Feature Usage Summary Cards */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(150px, 1fr))",
                      gap: "15px",
                      marginBottom: "25px",
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: "#f1f5f9",
                        padding: "15px",
                        borderRadius: "8px",
                        textAlign: "center",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "24px",
                          fontWeight: "bold",
                          color: "#1e293b",
                        }}
                      >
                        {analyticsData.featureUsage.totalFeatures}
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#64748b",
                          marginTop: "5px",
                        }}
                      >
                        Total Features
                      </div>
                    </div>
                    <div
                      style={{
                        backgroundColor: "#f0f9ff",
                        padding: "15px",
                        borderRadius: "8px",
                        textAlign: "center",
                        border: "1px solid #bae6fd",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "24px",
                          fontWeight: "bold",
                          color: "#0369a1",
                        }}
                      >
                        {analyticsData.featureUsage.totalUsage}
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#64748b",
                          marginTop: "5px",
                        }}
                      >
                        Total Usage
                      </div>
                    </div>
                    {analyticsData.featureUsage.topFeature && (
                      <div
                        style={{
                          backgroundColor: "#f0fdf4",
                          padding: "15px",
                          borderRadius: "8px",
                          textAlign: "center",
                          border: "1px solid #bbf7d0",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "bold",
                            color: "#166534",
                          }}
                        >
                          {analyticsData.featureUsage.topFeature.name}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#64748b",
                            marginTop: "5px",
                          }}
                        >
                          Most Used (
                          {analyticsData.featureUsage.topFeature.percentage}%)
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Pie Chart Visualization */}
                  <div
                    style={{
                      display: "flex",
                      gap: "30px",
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                    }}
                  >
                    {/* Simple Pie Chart */}
                    <div style={{ flex: "1", minWidth: "300px" }}>
                      <h4 style={{ marginBottom: "15px", color: "#374151" }}>
                        Feature Usage Distribution
                      </h4>
                      <div
                        style={{
                          position: "relative",
                          width: "200px",
                          height: "200px",
                          margin: "0 auto",
                          borderRadius: "50%",
                          background: `conic-gradient(${analyticsData.featureUsage.features
                            .slice(0, 8)
                            .map((feature, index) => {
                              const colors = [
                                "#3b82f6",
                                "#10b981",
                                "#f59e0b",
                                "#ef4444",
                                "#8b5cf6",
                                "#06b6d4",
                                "#84cc16",
                                "#f97316",
                              ];
                              const startAngle =
                                analyticsData.featureUsage.features
                                  .slice(0, index)
                                  .reduce(
                                    (sum, f) => sum + parseFloat(f.percentage),
                                    0
                                  ) * 3.6;
                              const endAngle =
                                startAngle +
                                parseFloat(feature.percentage) * 3.6;
                              return `${
                                colors[index % colors.length]
                              } ${startAngle}deg ${endAngle}deg`;
                            })
                            .join(", ")})`,
                          border: "3px solid white",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: "120px",
                            height: "120px",
                            backgroundColor: "white",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexDirection: "column",
                            boxShadow: "inset 0 2px 4px rgba(0,0,0,0.1)",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "18px",
                              fontWeight: "bold",
                              color: "#1f2937",
                            }}
                          >
                            {analyticsData.featureUsage.features.length}
                          </div>
                          <div style={{ fontSize: "10px", color: "#6b7280" }}>
                            Features
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Feature List */}
                    <div style={{ flex: "1", minWidth: "250px" }}>
                      <h4 style={{ marginBottom: "15px", color: "#374151" }}>
                        Top Features
                      </h4>
                      <div style={{ maxHeight: "200px", overflow: "auto" }}>
                        {analyticsData.featureUsage.features
                          .slice(0, 8)
                          .map((feature, index) => {
                            const colors = [
                              "#3b82f6",
                              "#10b981",
                              "#f59e0b",
                              "#ef4444",
                              "#8b5cf6",
                              "#06b6d4",
                              "#84cc16",
                              "#f97316",
                            ];
                            return (
                              <div
                                key={index}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  padding: "8px 0",
                                  borderBottom: "1px solid #f1f5f9",
                                }}
                              >
                                <div
                                  style={{
                                    width: "12px",
                                    height: "12px",
                                    backgroundColor:
                                      colors[index % colors.length],
                                    borderRadius: "2px",
                                    marginRight: "10px",
                                  }}
                                />
                                <div style={{ flex: 1 }}>
                                  <div
                                    style={{
                                      fontSize: "13px",
                                      fontWeight: "500",
                                      color: "#1f2937",
                                    }}
                                  >
                                    {feature.name}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "11px",
                                      color: "#6b7280",
                                    }}
                                  >
                                    {feature.count} uses • {feature.uniqueUsers}{" "}
                                    users
                                  </div>
                                </div>
                                <div
                                  style={{
                                    fontSize: "12px",
                                    fontWeight: "500",
                                    color: "#374151",
                                  }}
                                >
                                  {feature.percentage}%
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* All Users Table */}
              <div>
                <h3 style={{ marginBottom: "15px", color: "#333" }}>
                  All Users Summary
                </h3>
                <div
                  style={{
                    maxHeight: "300px",
                    overflow: "auto",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                  }}
                >
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr
                        style={{
                          backgroundColor: "#f1f5f9",
                          position: "sticky",
                          top: 0,
                        }}
                      >
                        <th
                          style={{
                            padding: "12px",
                            textAlign: "left",
                            borderBottom: "1px solid #ddd",
                          }}
                        >
                          User
                        </th>
                        <th
                          style={{
                            padding: "12px",
                            textAlign: "right",
                            borderBottom: "1px solid #ddd",
                          }}
                        >
                          Sessions
                        </th>
                        <th
                          style={{
                            padding: "12px",
                            textAlign: "right",
                            borderBottom: "1px solid #ddd",
                          }}
                        >
                          Total Time
                        </th>
                        <th
                          style={{
                            padding: "12px",
                            textAlign: "right",
                            borderBottom: "1px solid #ddd",
                          }}
                        >
                          Avg per Session
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {analyticsData.userStats
                        .sort((a, b) => b.totalTime - a.totalTime)
                        .map((user, index) => (
                          <tr
                            key={index}
                            style={{
                              backgroundColor:
                                index % 2 === 0 ? "#f8fafc" : "white",
                            }}
                          >
                            <td
                              style={{
                                padding: "12px",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              {user.email}
                            </td>
                            <td
                              style={{
                                padding: "12px",
                                textAlign: "right",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              {user.sessions}
                            </td>
                            <td
                              style={{
                                padding: "12px",
                                textAlign: "right",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              {formatTime(user.totalTime)}
                            </td>
                            <td
                              style={{
                                padding: "12px",
                                textAlign: "right",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              {formatTime(
                                Math.round(user.totalTime / user.sessions)
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Module Comparison Section */}
              {analyticsData.moduleComparison && (
                <div
                  style={{
                    backgroundColor: "white",
                    padding: "20px",
                    borderRadius: "10px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    marginTop: "30px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "20px",
                    }}
                  >
                    <h3
                      style={{
                        margin: 0,
                        color: "#374151",
                        fontSize: "18px",
                        fontWeight: "600",
                      }}
                    >
                      Module Usage Comparison
                    </h3>
                    <button
                      onClick={() =>
                        setShowModuleComparison(!showModuleComparison)
                      }
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "5px",
                        display: "flex",
                        alignItems: "center",
                        color: "#6b7280",
                      }}
                      title={
                        showModuleComparison
                          ? "Hide comparison"
                          : "Show comparison"
                      }
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        {showModuleComparison ? (
                          <>
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                            <line x1="1" y1="1" x2="23" y2="23" />
                          </>
                        ) : (
                          <>
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </>
                        )}
                      </svg>
                    </button>
                  </div>
                  {showModuleComparison && (
                    <>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: "20px",
                        }}
                      >
                        {["LensViewer", "LensLauncher"].map((moduleName) => {
                          const moduleData =
                            analyticsData.moduleComparison[moduleName];
                          const totalComparison =
                            (analyticsData.moduleComparison.LensViewer
                              ?.totalTime || 0) +
                            (analyticsData.moduleComparison.LensLauncher
                              ?.totalTime || 0);
                          const percentage =
                            totalComparison > 0
                              ? (
                                  ((moduleData?.totalTime || 0) /
                                    totalComparison) *
                                  100
                                ).toFixed(1)
                              : 0;

                          return (
                            <div
                              key={moduleName}
                              style={{
                                padding: "15px",
                                border: "2px solid #e5e7eb",
                                borderRadius: "8px",
                                backgroundColor:
                                  moduleName === "LensViewer"
                                    ? "#f0f9ff"
                                    : "#fef3c7",
                                borderColor:
                                  moduleName === "LensViewer"
                                    ? "#3b82f6"
                                    : "#f59e0b",
                              }}
                            >
                              <h4
                                style={{
                                  margin: "0 0 5px 0",
                                  color:
                                    moduleName === "LensViewer"
                                      ? "#1d4ed8"
                                      : "#d97706",
                                  fontSize: "16px",
                                  fontWeight: "600",
                                }}
                              >
                                {moduleName}
                              </h4>
                              <p
                                style={{
                                  margin: "0 0 10px 0",
                                  fontSize: "12px",
                                  color: "#6b7280",
                                  fontStyle: "italic",
                                }}
                              >
                                {moduleData?.functionality ||
                                  (moduleName === "LensViewer"
                                    ? "ViewerSession"
                                    : "LauncherSession")}
                              </p>
                              <div
                                style={{ fontSize: "14px", color: "#4b5563" }}
                              >
                                <p style={{ margin: "5px 0" }}>
                                  <strong>Total Time:</strong>{" "}
                                  {formatTime(moduleData?.totalTime || 0)}
                                </p>
                                <p style={{ margin: "5px 0" }}>
                                  <strong>Sessions:</strong>{" "}
                                  {moduleData?.totalSessions || 0}
                                </p>
                                <p style={{ margin: "5px 0" }}>
                                  <strong>Users:</strong>{" "}
                                  {moduleData?.uniqueUsers || 0}
                                </p>
                                <p style={{ margin: "5px 0" }}>
                                  <strong>Usage Share:</strong> {percentage}%
                                </p>
                              </div>
                              {/* Usage bar */}
                              <div
                                style={{
                                  width: "100%",
                                  height: "8px",
                                  backgroundColor: "#e5e7eb",
                                  borderRadius: "4px",
                                  marginTop: "10px",
                                  overflow: "hidden",
                                }}
                              >
                                <div
                                  style={{
                                    width: `${percentage}%`,
                                    height: "100%",
                                    backgroundColor:
                                      moduleName === "LensViewer"
                                        ? "#3b82f6"
                                        : "#f59e0b",
                                    transition: "width 0.3s ease",
                                  }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Winner Declaration */}
                      <div
                        style={{
                          marginTop: "20px",
                          padding: "15px",
                          backgroundColor: "#f3f4f6",
                          borderRadius: "8px",
                          textAlign: "center",
                        }}
                      >
                        {(() => {
                          const lensViewerTime =
                            analyticsData.moduleComparison.LensViewer
                              ?.totalTime || 0;
                          const lensLauncherTime =
                            analyticsData.moduleComparison.LensLauncher
                              ?.totalTime || 0;

                          if (lensViewerTime > lensLauncherTime) {
                            return (
                              <p
                                style={{
                                  margin: 0,
                                  color: "#1d4ed8",
                                  fontWeight: "600",
                                }}
                              >
                                ViewerSession has higher usage in this period
                              </p>
                            );
                          } else if (lensLauncherTime > lensViewerTime) {
                            return (
                              <p
                                style={{
                                  margin: 0,
                                  color: "#d97706",
                                  fontWeight: "600",
                                }}
                              >
                                LauncherSession has higher usage in this period
                              </p>
                            );
                          } else {
                            return (
                              <p
                                style={{
                                  margin: 0,
                                  color: "#6b7280",
                                  fontWeight: "600",
                                }}
                              >
                                Both functionalities have equal usage
                              </p>
                            );
                          }
                        })()}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Analytics;
